import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { useAuthStore } from '@/stores/authStore'
import { usersApi } from '@/api/users'
import { contractBalanceApi } from '@/api/contractBalance'
import { contractTransactionsApi } from '@/api/contractTransactions'
import { lessonSubscriptionsApi } from '@/api/lessonSubscriptions'
import type { ContractBalance, ContractTransaction, LessonSubscriptionPlan } from '@/types/billing'
import { useContractRealtime } from '@/realtime/useContractRealtime'
import { formatMoney } from '@/components/billing/formatMoney'
import { SubscriptionsBlock } from '@/components/billing/SubscriptionsBlock'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'
import '@/styles/dashboard.css'

export function StudentFinancePage() {
  const { t } = useTranslation()
  const authUser = useAuthStore((s) => s.user)
  const [contractId, setContractId] = useState<number | null>(null)
  const [balance, setBalance] = useState<ContractBalance | null>(null)
  const [transactions, setTransactions] = useState<ContractTransaction[]>([])
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [requestOpen, setRequestOpen] = useState(false)
  const [requestAmount, setRequestAmount] = useState('')
  const [requestDescription, setRequestDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [subsRefreshKey, setSubsRefreshKey] = useState(0)
  const [buyOpen, setBuyOpen] = useState(false)
  const [availablePlans, setAvailablePlans] = useState<LessonSubscriptionPlan[]>([])
  const [buyPlanId, setBuyPlanId] = useState<number | ''>('')
  const [buyStartsAt, setBuyStartsAt] = useState('')
  const [buySubmitting, setBuySubmitting] = useState(false)

  const loadTransactions = useCallback(
    async (contract: number, pageNum: number, append: boolean) => {
      const res = await contractTransactionsApi.index(contract, { page: pageNum, per_page: 20 })
      setTransactions((prev) => (append ? [...prev, ...res.data] : res.data))
      setPage(res.current_page)
      setLastPage(res.last_page)
    },
    []
  )

  const reload = useCallback(async () => {
    if (!authUser?.id) return
    setError(null)
    try {
      const user = await usersApi.getById(authUser.id)
      const cid = user.contract?.id ?? null
      setContractId(cid)
      if (!cid) {
        setBalance(null)
        setTransactions([])
        return
      }
      const bal = await contractBalanceApi.get(cid)
      setBalance(bal)
      await loadTransactions(cid, 1, false)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [authUser?.id, loadTransactions, t])

  useEffect(() => {
    reload().catch(() => {})
  }, [reload])

  useContractRealtime(contractId, () => {
    reload().catch(() => {})
    setSubsRefreshKey((v) => v + 1)
  })

  const handleStudentRequest = async () => {
    if (!contractId) return
    const amount = parseFloat(requestAmount)
    if (!Number.isFinite(amount) || !requestDescription.trim()) return
    setSubmitting(true)
    try {
      await contractTransactionsApi.studentRequest(contractId, {
        amount,
        description: requestDescription.trim(),
      })
      setRequestOpen(false)
      setRequestAmount('')
      setRequestDescription('')
      await reload()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setSubmitting(false)
    }
  }

  const openBuyModal = async () => {
    if (!contractId) return
    try {
      const plans = await lessonSubscriptionsApi.listAvailablePlans(contractId)
      setAvailablePlans(plans)
      setBuyPlanId(plans[0]?.id ?? '')
      setBuyStartsAt(new Date().toISOString().slice(0, 10))
      setBuyOpen(true)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    }
  }

  const handleBuySubscription = async () => {
    if (!contractId || !buyPlanId || !buyStartsAt) return
    setBuySubmitting(true)
    try {
      await lessonSubscriptionsApi.requestPurchase(contractId, {
        plan_id: Number(buyPlanId),
        starts_at: buyStartsAt,
      })
      setBuyOpen(false)
      await reload()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
    } finally {
      setBuySubmitting(false)
    }
  }

  const txTypeLabel = (tx: ContractTransaction): string => {
    if (tx.type === 'subscription_purchase' && tx.status === 'pending') {
      return t('billing.subscriptionPurchasePending', 'Покупка абонемента (ожидает подтверждения)')
    }
    return t(`billing.txType.${tx.type}`, tx.type)
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('billing.studentFinanceTitle', 'Финансы')}
          </Typography>
          <PageHelpPopover pageKey="student-finance" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!contractId && !loading ? (
          <Alert severity="info">{t('billing.noContract', 'Договор не найден')}</Alert>
        ) : loading ? (
          <LinearProgress />
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {t('billing.balance', 'Баланс')}
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {formatMoney(balance?.balance_amount ?? 0, balance?.currency ?? 'RUB')}
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" onClick={() => setRequestOpen(true)}>
                    {t('billing.reportPayment', 'Сообщить об оплате')}
                  </Button>
                  <Button variant="outlined" onClick={() => void openBuyModal()}>
                    {t('billing.buySubscription', 'Купить абонемент')}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {contractId && authUser?.id && (
              <SubscriptionsBlock
                contractId={contractId}
                studentId={authUser.id}
                readOnly
                refreshKey={subsRefreshKey}
              />
            )}

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('billing.transactionHistory', 'История операций')}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('billing.txDate', 'Дата')}</TableCell>
                      <TableCell>{t('billing.txTypeLabel', 'Тип')}</TableCell>
                      <TableCell>{t('billing.txAmount', 'Сумма')}</TableCell>
                      <TableCell>{t('billing.filterStatus', 'Статус')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell>{formatDateDMY(tx.created_at)}</TableCell>
                        <TableCell>{txTypeLabel(tx)}</TableCell>
                        <TableCell>
                          {tx.direction === 'debit' ? '−' : '+'}
                          {formatMoney(tx.amount, tx.currency)}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={t(`billing.txStatus.${tx.status}`, tx.status)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {page < lastPage && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button onClick={() => contractId && loadTransactions(contractId, page + 1, true)}>
                      {t('billing.loadMore', 'Загрузить ещё')}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('billing.reportPayment', 'Сообщить об оплате')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              size="small"
              type="number"
              label={t('billing.amount', 'Сумма')}
              value={requestAmount}
              onChange={(e) => setRequestAmount(e.target.value)}
            />
            <TextField
              size="small"
              multiline
              minRows={2}
              label={t('billing.paymentDescription', 'Комментарий (как перевели)')}
              value={requestDescription}
              onChange={(e) => setRequestDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
          <Button variant="contained" disabled={submitting} onClick={() => void handleStudentRequest()}>
            {t('common.send', 'Отправить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={buyOpen} onClose={() => setBuyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('billing.buySubscription', 'Купить абонемент')}</DialogTitle>
        <DialogContent>
          {availablePlans.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('billing.noPlansAvailable', 'Нет доступных абонементов')}
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t('billing.plan', 'Абонемент')}</InputLabel>
                <Select
                  label={t('billing.plan', 'Абонемент')}
                  value={buyPlanId}
                  onChange={(e) => setBuyPlanId(Number(e.target.value))}
                >
                  {availablePlans.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.title} — {p.lessons_count} {t('billing.lessonsShort', 'ур.')} ·{' '}
                      {formatMoney(p.total_price ?? null, p.currency)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label={t('billing.startsAt', 'Начало')}
                InputLabelProps={{ shrink: true }}
                value={buyStartsAt}
                onChange={(e) => setBuyStartsAt(e.target.value)}
              />
              <Alert severity="info">
                {t(
                  'billing.buySubscriptionHint',
                  'Заявка будет отправлена учителю на подтверждение. Абонемент станет активным после подтверждения.'
                )}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuyOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
          <Button
            variant="contained"
            disabled={buySubmitting || !buyPlanId || availablePlans.length === 0}
            onClick={() => void handleBuySubscription()}
          >
            {t('common.send', 'Отправить')}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}
